import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Paper,
  InputAdornment,
  TextField,
  IconButton,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import { makeStyles } from '@mui/styles';
import { Add as AddIcon, Clear as ClearIcon } from "@mui/icons-material";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { ReactComponent as InputSearchIcon } from "../assets/icons/icon-feather-search.svg";
import { ReactComponent as DeleteIcon } from "../assets/icons/icon-delete.svg";
import Page from '../components/Page';
import toast from 'react-hot-toast';
import { getDownloadReportList } from '../config/services/reports';
import { handleColumnDisplayName, mapResponseForQuery, reportFormatter } from '../utils/reportQueryBuilder';
import { initXLSXExportMultiple } from '../utils/ExportXLSX';

const useStyles = makeStyles((theme) => ({
    container: {
        paddingTop: '2rem',
        paddingBottom: '2rem',
    },
    headerSection: {
        marginBottom: '2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '1rem',
        },
    },
    title: {
        color: '#202124',
        fontSize: '30px',
        fontWeight: 'bold',
    },
    controlsSection: {
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'stretch',
        },
    },
    dropdown: {
        minWidth: '200px',
        [theme.breakpoints.down('sm')]: {
            minWidth: '100%',
        },
        '& .MuiSelect-select': {
            maxHeight: '200px',
            overflowY: 'auto',
        },
    },
    dropdownMenu: {
        '& .MuiPaper-root': {
            maxHeight: '300px',
        },
        '& .MuiList-root': {
            maxHeight: '250px',
            overflowY: 'auto',
        },
    },
    menuItemContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
    },
    menuItemTitle: {
        fontWeight: '500',
        fontSize: '14px',
        color: '#202124',
        lineHeight: '1.2',
    },
    menuItemSubtitle: {
        fontSize: '12px',
        color: '#666',
        marginTop: '2px',
        lineHeight: '1.2',
    },
    listContainer: {
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0px 3px 6px #00000029',
        overflow: 'hidden',
        marginBottom: '1rem',
    },
    listItem: {
        borderBottom: '1px solid #f0f0f0',
        '&:last-child': {
            borderBottom: 'none',
        },
        '&:hover': {
            backgroundColor: '#f5f5f5',
        },
    },
    searchField: {
        marginBottom: '1rem',
    },
    emptyState: {
        textAlign: 'center',
        padding: '3rem',
        color: '#666',
    },
    selectedCount: {
        color: '#1976d2',
        fontSize: '14px',
        marginLeft: '0.5rem',
    },
}));

const DownloadReports = () => {
    const classes = useStyles();
    const [selectedReportId, setSelectedReportId] = useState('');
    const [addedReports, setAddedReports] = useState([]);
    const [selectedForDownload, setSelectedForDownload] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [availableReports, setAvailableReports] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchAvailableReports = async () => {
        getDownloadReportList()?.then((data) => {
            setAvailableReports(data?.result || []);
        }).catch((error) => {
            toast.error('Failed to fetch reports. Please try again later.');
        });
    }

    useEffect(() => {
        fetchAvailableReports();
    }, []);
    

    const filteredReports = addedReports.filter(report =>
        (report.reportName || report.name || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleAddReport = () => {
        if (!selectedReportId) {
            toast.error('Please select a report to add');
            return;
        }

        const reportToAdd = availableReports.find(report => report._id === selectedReportId);
        if (reportToAdd && !addedReports.find(item => item._id === reportToAdd._id)) {
            setAddedReports([...addedReports, reportToAdd]);
            setSelectedReportId('');
            toast.success(`${reportToAdd.reportName} added to download list`);
        } else {
            toast.error('Report already exists in the list');
        }
    };

    const handleReportSelect = (report, checked) => {
        if (checked) {
            setSelectedForDownload([...selectedForDownload, report]);
        } else {
            setSelectedForDownload(selectedForDownload.filter(item => item?._id !== report._id));
        }
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedForDownload(filteredReports);
        } else {
            setSelectedForDownload([]);
        }
    };

    const handleRemoveReport = (reportId) => {
        setAddedReports(addedReports.filter(report => report._id !== reportId));
        setSelectedForDownload(selectedForDownload.filter(item => item._id !== reportId));
        toast.success('Report removed from list');
    };

    const handleDownload = async () => {
       try {
         if (selectedForDownload.length === 0) {
           toast.error("Please select at least one report to download");
           return;
         }
         const formattedReports = reportFormatter(
           JSON.parse(JSON.stringify(selectedForDownload))
         );
         setLoading(true);
         await initXLSXExportMultiple(
           formattedReports,
           mapResponseForQuery,
           handleColumnDisplayName
         );
         setLoading(false);
       } catch (error) {
         toast.error("Failed to download reports. Please try again later.");
         setLoading(false);
       }
};

    return (
      <Page title="Download Reports">
        <Container maxWidth="lg" className={classes.container}>
          <Box className={classes.headerSection}>
            <Typography variant="h4" className={classes.title}>
              Download Reports
            </Typography>
            <Button
              variant="contained"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={handleDownload}
              disabled={selectedForDownload.length === 0}
            >
              Download Selected ({selectedForDownload.length})
            </Button>
          </Box>

          <Box className={classes.controlsSection}>
            <Autocomplete
              options={availableReports.filter(
                (report) =>
                  !addedReports.find((item) => item._id === report._id)
              )}
              getOptionLabel={(option) => option.reportName || ""}
              filterOptions={(options, { inputValue }) =>
                options
                  .filter((option) =>
                    (option.reportName || "")
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  )
                  .slice(0, 50)
              }
              value={
                availableReports.find((r) => r._id === selectedReportId) || null
              }
              onChange={(event, newValue) => {
                setSelectedReportId(newValue ? newValue._id : "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select or search a report to add"
                  variant="outlined"
                  size="small"
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option._id}>
                  <Box className={classes.menuItemContent}>
                    <Typography className={classes.menuItemTitle}>
                      {option.reportName}
                    </Typography>
                    <Typography className={classes.menuItemSubtitle}>
                      Created by: {option.createdBy || "Unknown"} | Last
                      modified:{" "}
                      {new Date(option.updatedAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </li>
              )}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              noOptionsText={
                availableReports.length === 0
                  ? "No reports available"
                  : "No matching reports found"
              }
              style={{ minWidth: 500, maxWidth: 400 }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddReport}
              disabled={!selectedReportId}
            >
              Add Report
            </Button>
            <Button
              variant="contained"
              startIcon={<ClearIcon />}
              onClick={() => {
                setAddedReports([]);
                setSelectedForDownload([]);
                setSelectedReportId("");
              }}
              disabled={filteredReports.length === 0}
            >
              Reset
            </Button>
          </Box>

          <TextField
            className={classes.searchField}
            placeholder="Search reports..."
            variant="outlined"
            size="medium"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <InputSearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <Paper className={classes.listContainer}>
            {filteredReports.length > 0 ? (
              <>
                <ListItem className={classes.listItem}>
                  <ListItemIcon>
                    <Checkbox
                      checked={
                        selectedForDownload.length === filteredReports.length &&
                        filteredReports.length > 0
                      }
                      indeterminate={
                        selectedForDownload.length > 0 &&
                        selectedForDownload.length < filteredReports.length
                      }
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        variant="subtitle1"
                        style={{ fontWeight: "bold" }}
                      >
                        Select All
                        <span className={classes.selectedCount}>
                          ({selectedForDownload.length} of{" "}
                          {filteredReports.length} selected)
                        </span>
                      </Typography>
                    }
                  />
                </ListItem>

                {filteredReports.map((report) => (
                  <ListItem key={report._id} className={classes.listItem}>
                    <ListItemIcon>
                      <Checkbox
                        checked={selectedForDownload.includes(report)}
                        onChange={(e) =>
                          handleReportSelect(report, e.target.checked)
                        }
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={report.reportName}
                      secondary={`Updated: ${new Date(
                        report.updatedAt
                      ).toLocaleDateString()} | Created by: ${
                        report.createdBy || "Unknown"
                      }`}
                    />
                    <IconButton
                      onClick={() => handleRemoveReport(report._id)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItem>
                ))}
              </>
            ) : addedReports.length === 0 ? (
              <Box className={classes.emptyState}>
                <Typography variant="h6" color="textSecondary">
                  No reports added yet
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Select a report from the dropdown above and click "Add Report"
                  to get started.
                </Typography>
              </Box>
            ) : (
              <Box className={classes.emptyState}>
                <Typography variant="h6" color="textSecondary">
                  No reports found
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Try adjusting your search criteria.
                </Typography>
              </Box>
            )}
          </Paper>
          <Dialog
            aria-labelledby="customized-dialog-title"
            open={loading}
          >
            <DialogTitle id="customized-dialog-title">
              Export in Progress...{" "}
            </DialogTitle>
            <DialogContent dividers>
              <Typography>
                {" "}
                Preparing your excel file... <CircularProgress />{" "}
              </Typography>
            </DialogContent>
          </Dialog>
        </Container>
      </Page>
    );
};

export default DownloadReports;